import { Navigate, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useActiveChargingStore } from '../store/activeChargingStore';
import { useActiveChargingSession } from '../hooks/useActiveChargingSession';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { BottomNav } from '../app/components/BottomNav';
import { BottomNavCharging } from '../app/components/BottomNavCharging';
import { QRScanner, type QRScanResult } from '../app/components/QRScanner';
import { nextSearchAfterScannerClose, scanResultToPath } from '../utils/qrScanNavigation';
import { Loader2 } from 'lucide-react';
import { useCallback, useMemo, useRef } from 'react';

/**
 * ProtectedLayout
 *
 * Auth guard and app layout wrapper for all authenticated routes.
 * - Checks authentication status
 * - Shows loading spinner while checking auth
 * - Redirects to /login if not authenticated (saves return URL)
 * - Renders page content via <Outlet />
 * - Shows BottomNav with active tab synced to URL
 * - Shows BottomNavCharging when there's an active charging session
 * - Owns the QR scanner, so it can be opened from the nav over any tab
 */
export function ProtectedLayout() {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-navigate to summary when a transaction stops (unless already on its charging status page)
  const handleTransactionStopped = useCallback((transactionId: string) => {
    const isOnChargingStatus = location.pathname === `/app/activity/charging/${transactionId}`;
    if (!isOnChargingStatus) {
      console.log('[ProtectedLayout] Transaction stopped, navigating to summary:', transactionId);
      navigate(`/app/activity/summary/${transactionId}`);
    }
  }, [location.pathname, navigate]);

  // Initialize active charging session tracking (fetches from API + polling)
  useActiveChargingSession({ onTransactionStopped: handleTransactionStopped });

  // Initialize push notifications (FCM token registration, listeners)
  usePushNotifications();

  // Get active charging session state
  const {
    hasActiveSession,
    transactionId,
    batteryLevel,
    energyConsumed,
  } = useActiveChargingStore();

  // Extract active tab from URL path
  const activeTab = useMemo(() => {
    const pathSegments = location.pathname.split('/');
    const tab = pathSegments[2] as 'map' | 'activity' | 'wallet' | 'notifications' | 'profile';
    return tab || 'map';
  }, [location.pathname]);

  /**
   * The scanner is a `?qr=true` overlay rather than a route of its own, so it
   * opens over whichever tab is showing and closing it drops the param and
   * leaves you exactly where you were — including on top of an open location
   * sheet, where the old map-only FAB was unreachable.
   */
  const showQR = searchParams.get('qr') === 'true';
  // A successful scan navigates away (and may carry ?gun=). The close that
  // follows must not rewrite search from the stale ?qr=true snapshot.
  const scanNavigatedRef = useRef(false);

  const openScanner = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('qr', 'true');
      return next;
    });
  }, [setSearchParams]);

  const handleScannerOpenChange = useCallback(
    (open: boolean) => {
      if (open) return;
      const next = nextSearchAfterScannerClose(location.search, {
        didScanNavigate: scanNavigatedRef.current,
      });
      scanNavigatedRef.current = false;
      if (next === null) return;
      setSearchParams(new URLSearchParams(next), { replace: true });
    },
    [location.search, setSearchParams]
  );

  const handleQRScanSuccess = useCallback(
    (result: QRScanResult) => {
      scanNavigatedRef.current = true;
      navigate(scanResultToPath(result));
    },
    [navigate]
  );

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <Loader2 className="w-8 h-8 animate-spin text-[#8bc43e]" />
      </div>
    );
  }

  // Redirect to login if not authenticated (save return URL)
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Handler for clicking the active charging bar
  const handleActiveSessionClick = () => {
    if (transactionId) {
      // Navigate to the charging status page for this transaction
      navigate(`/app/activity/charging/${transactionId}`);
    }
  };

  return (
    <div className="h-dvh w-full flex flex-col bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 transition-colors pt-[env(safe-area-inset-top)]">
      {/* Page content rendered by nested routes */}
      <main className="flex-1 overflow-hidden relative">
        <Outlet />
      </main>

      {/* Bottom Navigation */}
      {hasActiveSession ? (
        <BottomNavCharging
          activeTab={activeTab}
          onTabChange={(tab) => navigate(`/app/${tab}`)}
          hasActiveSession={hasActiveSession}
          batteryLevel={batteryLevel}
          energyConsumed={energyConsumed}
          onChargingClick={handleActiveSessionClick}
          onScan={openScanner}
        />
      ) : (
        <BottomNav
          activeTab={activeTab}
          onTabChange={(tab) => navigate(`/app/${tab}`)}
          onScan={openScanner}
        />
      )}

      <QRScanner
        open={showQR}
        onOpenChange={handleScannerOpenChange}
        onScanSuccess={handleQRScanSuccess}
      />
    </div>
  );
}
